import { Centrifuge, type Subscription } from 'centrifuge';

export type PlaeeClientStatus = 'idle' | 'connecting' | 'connected' | 'disconnected' | 'error';

/**
 * Auth bundle returned by `POST /prediction-market/realtime/auth`. The
 * Centrifugo JWT goes into Centrifuge; the customerId is cached on the client
 * so the {@link RealtimePrivateBridge} can target the `user:#<customerId>`
 * private channel without re-fetching.
 */
export interface PlaeeAuthBundle {
  token: string | null;
  plaeeCustomerId: string | null;
}

export interface PlaeeClientOptions {
  /** Centrifugo WS URL. e.g. `wss://centrifugo.plaee.com/connection/websocket`. */
  url: string;
  /**
   * Returns a fresh Centrifugo JWT + the Plaee customer id, or `{ token: null,
   * plaeeCustomerId: null }` when the user is anonymous. Centrifuge invokes
   * this on initial connect and on token refresh just before TTL expiry.
   */
  getAuth: () => Promise<PlaeeAuthBundle>;
  onStatus?: (status: PlaeeClientStatus) => void;
  onError?: (err: unknown) => void;
}

type AuthListener = (bundle: PlaeeAuthBundle) => void;

/**
 * Thin singleton around the `centrifuge` client. Owns one connection per app
 * lifetime; both public channels (no auth) and the user's private channel
 * (token-gated) share it.
 *
 * Connection is lazy — the first {@link newSubscription} call triggers connect.
 * Token presence flips between anonymous and authed via {@link setAuthToken}:
 * the next reconnect (or token-refresh callback) picks up the new value.
 */
export class PlaeeClient {
  private centrifuge: Centrifuge | null = null;
  private readonly opts: PlaeeClientOptions;
  private cachedToken: string | null = null;
  private cachedCustomerId: string | null = null;
  private readonly authListeners = new Set<AuthListener>();

  constructor(opts: PlaeeClientOptions) {
    this.opts = opts;
  }

  /**
   * Update the cached token. Does not force a reconnect — the next
   * `getAuth` evaluation picks it up, either on initial connect or on
   * Centrifuge's TTL-driven refresh.
   */
  setAuthToken(token: string | null): void {
    this.cachedToken = token;
  }

  /** Latest Plaee customer id seen on an auth response, if any. */
  getPlaeeCustomerId(): string | null {
    return this.cachedCustomerId;
  }

  /**
   * Force an auth fetch and return the resulting bundle. Used by consumers
   * that need the customerId before any public subscription opens (which
   * would otherwise be the first time `getAuth` runs).
   */
  async ensureAuth(): Promise<PlaeeAuthBundle> {
    const bundle = await this.opts.getAuth();
    this.applyAuth(bundle);
    return bundle;
  }

  /**
   * Subscribe to auth bundle updates. Fires immediately if a bundle has
   * already been resolved. Returns an unsubscribe function.
   */
  onAuth(listener: AuthListener): () => void {
    this.authListeners.add(listener);
    if (this.cachedToken !== null || this.cachedCustomerId !== null) {
      listener({ token: this.cachedToken, plaeeCustomerId: this.cachedCustomerId });
    }
    return () => {
      this.authListeners.delete(listener);
    };
  }

  newSubscription(channel: string): Subscription {
    return this.ensureClient().newSubscription(channel);
  }

  getExistingSubscription(channel: string): Subscription | null {
    return this.centrifuge?.getSubscription(channel) ?? null;
  }

  /**
   * Flush a {@link Subscription} from Centrifuge's internal registry. Required
   * after `sub.unsubscribe()` — without this call, the sub stays in
   * `unsubscribed` state inside the registry, and a subsequent
   * {@link getExistingSubscription} returns the orphan instead of letting us
   * mint a fresh one.
   */
  removeSubscription(sub: Subscription): void {
    try {
      this.centrifuge?.removeSubscription(sub);
    } catch {
      // Best-effort.
    }
  }

  disconnect(): void {
    try {
      this.centrifuge?.disconnect();
    } catch {
      // Best-effort.
    }
    this.centrifuge = null;
  }

  private applyAuth(bundle: PlaeeAuthBundle): void {
    this.cachedToken = bundle.token;
    if (bundle.plaeeCustomerId !== null) {
      this.cachedCustomerId = bundle.plaeeCustomerId;
    }
    for (const l of this.authListeners) {
      try {
        l(bundle);
      } catch (err) {
        console.warn('[plaee-client] auth listener threw', err);
      }
    }
  }

  private ensureClient(): Centrifuge {
    if (this.centrifuge !== null) return this.centrifuge;

    this.opts.onStatus?.('connecting');

    const client = new Centrifuge(this.opts.url, {
      getToken: async () => {
        try {
          const bundle = await this.opts.getAuth();
          this.applyAuth(bundle);
          return bundle.token ?? '';
        } catch (err) {
          this.opts.onError?.(err);
          throw err;
        }
      },
      token: this.cachedToken ?? undefined,
      debug: import.meta.env.DEV,
    });

    client.on('connecting', (ctx: { code?: number; reason?: string }) =>
      console.info('[plaee-client] connecting', ctx.code, ctx.reason),
    );
    client.on('connected', (ctx: { transport?: string }) => console.info('[plaee-client] connected via', ctx.transport));
    client.on('disconnected', (ctx: { code?: number; reason?: string }) => {
      console.warn('[plaee-client] disconnected', ctx.code, ctx.reason);
      this.opts.onStatus?.('disconnected');
    });
    client.on('error', (ev: { error?: { code?: number; message?: string } }) => {
      console.error('[plaee-client] error', ev.error?.code, ev.error?.message, ev);
      this.opts.onStatus?.('error');
      this.opts.onError?.(ev);
    });

    this.centrifuge = client;
    client.connect();
    return client;
  }
}
